/**
 * Deterministic mock schedule data, ported from the design handoff
 * (`schedule.jsx` → `makeMockSchedule`). Used until the live schedule data
 * source (org/location → classes API) is wired into <zipper-schedule>.
 */
export type CapacityState = "open" | "filling" | "almost-full" | "waitlist";

export interface MockClass {
  offeringKind?: "class" | "event" | "program";
  programName?: string;
  programSessionOrdinal?: number;
  programSessionCount?: number;
  programBookingUrl?: string;
  /** Enabled only when the activity supplies a safe external booking URL. */
  externalTickets?: boolean;
  externalTicketUrl?: string;
  id: string;
  date: Date;
  time: string;
  duration: string;
  name: string;
  /** Opaque admin-set class color; Calendar bars/dots use it when valid. */
  color?: string | undefined;
  instructor: string;
  location: string;
  /** Full formatted address for the location, used for the Maps link-out.
   * Absent = fall back to linking on the location name. */
  locationAddress?: string;
  capacityState: CapacityState;
  /** Remaining spots; -1 means unknown (hidden by the component). */
  spotsLeft: number;
  /** Total capacity for this activity — used for the low-availability "fire"
   * threshold (spotsLeft <= 2 OR spotsLeft/spotsTotal <= 33%), matching the
   * dashboard-customer flow. Absent = fall back to the spotsLeft <= 2 rule. */
  spotsTotal?: number;
  /** Derived authoritative whole-session option; absent/false means hidden. */
  buyoutAvailable?: boolean;
  /** Flat whole-session price in the organization's currency minor unit. */
  buyoutPriceMinor?: number;
  /** ISO-4217 code for buyoutPriceMinor. */
  buyoutCurrency?: string;
  cancelled: boolean;
  cancelReason?: string;
  /** Real Stripe product id for the drop-in charge (live data). */
  stripeProductId?: string;
  /** Spot/seat booking (live data): room to render the seat map for + the instance window. */
  spotBookingEnabled?: boolean;
  roomId?: string;
  startIso?: string;
  endIso?: string;
  openRegistrationTimeFrame?: boolean;
  registrationClosesAt?: string | null;
  /** Absolute opening instant; absent for unrestricted registration. */
  registrationOpenAt?: string | null;
  /** IANA timezone of the class's LOCATION (live: ActivityDto.timezone). Used
   * to render times in the studio's local time by default, and as the "off"
   * side of the N2 timezone switcher (viewer-local being the "on" side). Absent
   * → times fall back to the viewer's local zone. */
  timezone?: string;
  /** Event slug for post-charge registration (live data; id is the activity id). */
  eventSlug?: string;
  /** Entity forms (waiver/intake) attached to this class (live data). */
  forms?: { formId: number; type: string; requirement?: string }[];
  /** Instructor headshot URL (handoff INSTRUCTORS map; empty in live until wired). */
  photo?: string;
  /** Class/session's own image (distinct from the instructor headshot above) —
   * shown when the admin config's `thumbnail` option is set to `'class'`, and
   * on the booking checkout card. */
  classPhoto?: string;
  /** Plain-text session description (mock data only — set directly here since
   * there's no live API backing mock rows). */
  description?: string;
  /** Live data: whether this class has description text worth expanding for.
   * The text itself isn't loaded until the row expands (see
   * fetchEventDescription in live.ts) — the bulk schedule fetch only gets this
   * boolean flag, not the (potentially large) HTML content. */
  hasDescription?: boolean;
  /** Instructor bio (SCH-08) — sanitized WYSIWYG HTML for the first instructor
   * on this class that has one, sourced from the events-filter-data payload
   * (providerUsers[].biography). Rendered inline in the bio accordion. */
  instructorBio?: string;
  /** Live data: whether an instructor bio exists to expand for — the boolean
   * the schedule gates the instructor bio-accordion affordance on. */
  hasInstructorBio?: boolean;
  /** ST1 pre-filter (live data): the event-grouping id this class belongs to —
   * matched against the settings-selected `filterClassIds`. Absent on mock rows
   * (they aren't grouping-backed), so those simply aren't narrowed by it. */
  eventGroupingId?: string;
  /** Standalone one-off event (no grouping, no parent series) — additive when
   * `includeOneOffEvents` + class/grouping pre-filters are active. */
  isStandaloneOneOff?: boolean;
  /** ST1 pre-filter (live data): the provider (instructor) user ids on this
   * class — matched against the settings-selected `filterInstructors`. */
  providerUserIds?: number[];
  /** ST1 pre-filter (live data): the location id this class is at — matched
   * against the settings-selected `filterLocations`. */
  locationId?: string;
  /** C723-08: true when a substitute instructor is teaching this session. */
  hasSubstitute?: boolean;
}

/** Instructor headshots, ported from the handoff `INSTRUCTORS` map. */
export const INSTRUCTOR_PHOTOS: Record<string, string> = {
  "Marisol Pena":
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&h=200&q=80",
  "Lena Ortiz":
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&h=200&q=80",
  "Yasmin Castillo":
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&h=200&q=80",
  "Anika Rao":
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&h=200&q=80",
};

/** Class/session images, keyed by class name — distinct from instructor
 * headshots above. Shown when the `thumbnail` option is `'class'` and on the
 * booking checkout card. */
export const CLASS_PHOTOS: Record<string, string> = {
  "Reformer Flow":
    "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=400&h=400&q=80",
  "Slow Flow":
    "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=400&h=400&q=80",
  "Express Reformer":
    "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=400&h=400&q=80",
  "Reformer Sculpt":
    "https://images.unsplash.com/photo-1518310383802-640c2de311b2?auto=format&fit=crop&w=400&h=400&q=80",
  "Mat Foundations":
    "https://images.unsplash.com/photo-1591291621164-2c6367723315?auto=format&fit=crop&w=400&h=400&q=80",
  "Power Reformer":
    "https://images.unsplash.com/photo-1518459031867-a89b944bffe4?auto=format&fit=crop&w=400&h=400&q=80",
  "Reformer Privates":
    "https://images.unsplash.com/photo-1540496905036-5937c10647cc?auto=format&fit=crop&w=400&h=400&q=80",
  "Reformer + Mat":
    "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&w=400&h=400&q=80",
  "Sat Reformer Mix":
    "https://images.unsplash.com/photo-1571388208497-71bedc66e932?auto=format&fit=crop&w=400&h=400&q=80",
  "Sunday Slow Flow":
    "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=400&h=400&q=80",
};

/** Session descriptions, keyed by class name — shown when a row's expand
 * chevron is clicked. */
export const CLASS_DESCRIPTIONS: Record<string, string> = {
  "Reformer Flow":
    "A dynamic, full-body reformer class linking breath to movement. Expect a steady, flowing pace that builds core strength and control from head to toe.",
  "Slow Flow":
    "A slower-tempo, mat-based practice focused on alignment and mobility. Great for building foundational strength without high impact.",
  "Express Reformer":
    "A condensed reformer session hitting the essentials — core, legs, and posture — for days when you need a full workout in less time.",
  "Reformer Sculpt":
    "Reformer-based resistance training with light springs and higher reps to sculpt and tone through the arms, legs, and core.",
  "Mat Foundations":
    "A beginner-friendly mat class covering core Pilates principles: breath, alignment, and control. No equipment needed.",
  "Power Reformer":
    "An energetic, up-tempo reformer class with dynamic transitions and heavier spring loads for an intense full-body burn.",
  "Reformer Privates":
    "One-on-one reformer coaching tailored to your goals, injuries, or experience level. Limited to a single spot per session.",
  "Reformer + Mat":
    "A hybrid class combining reformer segments with mat-based finishers, targeting core and stability from every angle.",
  "Sat Reformer Mix":
    "A weekend reformer class blending strength, stretch, and cardio intervals for a well-rounded Saturday session.",
  "Sunday Slow Flow":
    "An unhurried Sunday mat flow focused on breath work, mobility, and gentle strengthening to close out the week.",
};

/** Fixed "today" so the mock renders identically every load. */
export const SCH_TODAY = new Date(2026, 4, 20); // Wed May 20, 2026

/** The mock studios (Allendale/Wyckoff/Ho-Ho-Kus, NJ) live in US Eastern — used
 * so the mock schedule exercises the N2 timezone switcher for any viewer whose
 * local zone differs from Eastern. */
const MOCK_TZ = "America/New_York";

/** Minutes `tz` is ahead of UTC at `date` (DST-aware via Intl). */
function tzOffsetMinutes(date: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asUtc - date.getTime()) / 60000);
}

/** Build the UTC instant that reads as `timeStr` (e.g. "7:00 AM") wall-clock on
 * `day` in `tz`. Lets the mock carry a real `startIso` so the timezone switcher
 * can re-render each time in the viewer's zone. */
function isoForWallClock(day: Date, timeStr: string, tz: string): string {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timeStr.trim());
  if (!m) return "";
  let hour = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) hour += 12;
  const minute = Number(m[2]);
  const guess = Date.UTC(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    hour,
    minute,
  );
  const offset = tzOffsetMinutes(new Date(guess), tz);
  return new Date(guess - offset * 60000).toISOString();
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/**
 * Build the demo schedule, laid out relative to `anchor` (day 0).
 *
 * `anchor` defaults to the fixed {@link SCH_TODAY} so callers that want a
 * byte-identical fixture (tests, the design handoff) still get one. The
 * component passes the real current day instead, so the local demo harness —
 * the only remaining caller, since a configured org never falls back to mock
 * data — doesn't render a schedule that is entirely in the past.
 */
export function makeMockSchedule(anchor: Date = SCH_TODAY): MockClass[] {
  const make = (
    dayOffset: number,
    time: string,
    duration: string,
    name: string,
    instructor: string,
    location: string,
    capacityState: CapacityState,
    spotsLeft: number,
    cancelled = false,
    cancelReason?: string,
  ): MockClass => ({
    id: `${dayOffset}-${time}-${name}-${location}`,
    date: addDays(anchor, dayOffset),
    time,
    duration,
    name,
    instructor,
    location,
    capacityState,
    spotsLeft,
    cancelled,
    cancelReason,
    photo: INSTRUCTOR_PHOTOS[instructor],
    classPhoto: CLASS_PHOTOS[name],
    description: CLASS_DESCRIPTIONS[name],
    // N2: carry a real instant + the studio's zone so times can be re-rendered
    // in the viewer's own timezone via the switcher (default stays Eastern).
    timezone: MOCK_TZ,
    startIso: isoForWallClock(addDays(anchor, dayOffset), time, MOCK_TZ),
  });

  return [
    make(
      0,
      "7:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Allendale",
      "open",
      8,
    ),
    make(
      0,
      "9:30 AM",
      "60 min",
      "Slow Flow",
      "Lena Ortiz",
      "Wyckoff",
      "filling",
      4,
    ),
    make(
      0,
      "12:00 PM",
      "45 min",
      "Express Reformer",
      "Marisol Pena",
      "Allendale",
      "almost-full",
      2,
    ),
    make(
      0,
      "5:30 PM",
      "50 min",
      "Reformer Sculpt",
      "Yasmin Castillo",
      "Ho-Ho-Kus",
      "waitlist",
      5,
    ),
    make(
      0,
      "7:00 PM",
      "60 min",
      "Mat Foundations",
      "Anika Rao",
      "Allendale",
      "open",
      12,
      true,
      "Instructor out sick",
    ),
    make(
      1,
      "6:30 AM",
      "50 min",
      "Power Reformer",
      "Marisol Pena",
      "Allendale",
      "open",
      6,
    ),
    make(
      1,
      "8:00 AM",
      "60 min",
      "Slow Flow",
      "Lena Ortiz",
      "Wyckoff",
      "open",
      9,
    ),
    make(
      1,
      "12:00 PM",
      "45 min",
      "Express Reformer",
      "Marisol Pena",
      "Ho-Ho-Kus",
      "filling",
      3,
    ),
    make(
      1,
      "6:00 PM",
      "50 min",
      "Reformer Flow",
      "Yasmin Castillo",
      "Allendale",
      "waitlist",
      0,
    ),
    make(
      2,
      "7:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Wyckoff",
      "open",
      10,
    ),
    make(
      2,
      "9:00 AM",
      "50 min",
      "Reformer Sculpt",
      "Yasmin Castillo",
      "Allendale",
      "filling",
      4,
      true,
      "Studio maintenance",
    ),
    make(
      2,
      "5:30 PM",
      "50 min",
      "Reformer Sculpt",
      "Lena Ortiz",
      "Allendale",
      "open",
      7,
    ),
    make(
      3,
      "7:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Allendale",
      "filling",
      4,
    ),
    make(
      3,
      "9:30 AM",
      "60 min",
      "Slow Flow",
      "Anika Rao",
      "Ho-Ho-Kus",
      "open",
      8,
    ),
    make(
      3,
      "5:30 PM",
      "50 min",
      "Reformer Sculpt",
      "Yasmin Castillo",
      "Wyckoff",
      "open",
      9,
    ),
    make(
      4,
      "8:00 AM",
      "60 min",
      "Reformer Privates",
      "Lena Ortiz",
      "Allendale",
      "open",
      1,
    ),
    make(
      4,
      "10:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Wyckoff",
      "filling",
      3,
    ),
    make(
      4,
      "6:00 PM",
      "50 min",
      "Reformer Sculpt",
      "Yasmin Castillo",
      "Ho-Ho-Kus",
      "open",
      5,
    ),
    make(
      5,
      "9:00 AM",
      "60 min",
      "Sat Reformer Mix",
      "Marisol Pena",
      "Allendale",
      "almost-full",
      2,
    ),
    make(
      5,
      "10:30 AM",
      "60 min",
      "Reformer + Mat",
      "Anika Rao",
      "Wyckoff",
      "open",
      9,
    ),
    make(
      6,
      "9:30 AM",
      "60 min",
      "Sunday Slow Flow",
      "Lena Ortiz",
      "Allendale",
      "open",
      11,
    ),
    make(
      7,
      "7:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Allendale",
      "open",
      10,
    ),
    make(
      7,
      "5:30 PM",
      "50 min",
      "Reformer Sculpt",
      "Yasmin Castillo",
      "Ho-Ho-Kus",
      "filling",
      4,
    ),
    make(
      8,
      "8:00 AM",
      "60 min",
      "Slow Flow",
      "Anika Rao",
      "Allendale",
      "open",
      9,
    ),
    make(
      9,
      "7:00 AM",
      "50 min",
      "Power Reformer",
      "Marisol Pena",
      "Allendale",
      "filling",
      5,
    ),
    make(
      10,
      "7:00 AM",
      "50 min",
      "Reformer Flow",
      "Marisol Pena",
      "Allendale",
      "open",
      9,
    ),
    make(
      11,
      "8:00 AM",
      "60 min",
      "Reformer Privates",
      "Lena Ortiz",
      "Allendale",
      "open",
      1,
    ),
    make(
      12,
      "9:00 AM",
      "60 min",
      "Sat Reformer Mix",
      "Marisol Pena",
      "Allendale",
      "open",
      8,
    ),
    make(
      13,
      "9:30 AM",
      "60 min",
      "Sunday Slow Flow",
      "Lena Ortiz",
      "Allendale",
      "open",
      11,
    ),
  ];
}
